/* ─────────────────────────────────────────────────────────────
   Shared status / priority / type chips — ctAS §8.6 taxonomy.
   Always text + color (never color alone) so they survive
   monochrome and color-blind themes. Colors are --scpa-status-*
   / --scpa-primary / --scpa-text-muted.
───────────────────────────────────────────────────────────── */

type ChipColor = 'ok' | 'error' | 'warning' | 'primary' | 'muted';

const COLOR_VAR: Record<ChipColor, string> = {
  ok: 'var(--scpa-status-ok)',
  error: 'var(--scpa-status-error)',
  warning: 'var(--scpa-status-warning)',
  primary: 'var(--scpa-primary)',
  muted: 'var(--scpa-text-muted)',
};

/** Resolve a def color — a theme tone name or a raw hex — to a CSS color. */
export function cssColor(color: string): string {
  return color.startsWith('#') ? color : COLOR_VAR[color as ChipColor] ?? COLOR_VAR.muted;
}

function Chip({ label, color }: { label: string; color: string }) {
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color: cssColor(color), whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

// ── Custom status registry ──────────────────────────────────
// Per-company status defs (label + color per key), fed by useTicketStatuses.
// Components that render badges call that hook, so re-renders happen when the
// defs load; the registry just makes the mapping reachable from deep leaves.
let STATUS_DEFS = new Map<string, { label: string; color: string }>();

export function registerStatusDefs(defs: Array<{ key: string; label: string; color: string }>): void {
  STATUS_DEFS = new Map(defs.map(d => [d.key, { label: d.label, color: d.color }]));
}

const STATUS_MAP: Record<string, { label: string; color: ChipColor }> = {
  new:               { label: 'New',                 color: 'primary' },
  open:              { label: 'Open',                color: 'primary' },
  waiting_internal:  { label: 'In progress',        color: 'warning' },
  waiting_customer:  { label: 'Waiting on customer', color: 'warning' },
  escalated:         { label: 'Escalated',          color: 'error'   },
  resolved:          { label: 'Resolved',           color: 'ok'      },
  closed:            { label: 'Closed',             color: 'ok'      },
  // transient sentinels
  submitting:        { label: 'Sending…',           color: 'muted'   },
  local_only:        { label: 'Recorded',           color: 'muted'   },
  send_failed:       { label: 'Retrying',           color: 'warning' },
};

export function StatusBadge({ status }: { status: string }) {
  const m = STATUS_DEFS.get(status)
    ?? STATUS_MAP[status]
    ?? { label: (status || '—').replace(/_/g, ' '), color: 'muted' as ChipColor };
  return <Chip label={m.label} color={m.color} />;
}

// ── Custom classification registry ──────────────────────────
// Per-company type/priority/source/impact/environment defs, fed by
// useClassifications. Same trick as the status registry above: components
// call the hook (so re-renders happen when the defs load) and the registry
// makes the label/color mapping reachable from deep leaves.
let CLASSIFICATION_DEFS = new Map<string, { label: string; color: string }>();

export function registerClassificationDefs(
  defs: Array<{ kind: string; key: string; label: string; color: string }>,
): void {
  CLASSIFICATION_DEFS = new Map(defs.map(d => [`${d.kind}:${d.key}`, { label: d.label, color: d.color }]));
}

/** A company's label + color for one classification value, if it has one. */
function classificationDef(kind: string, key: string) {
  return CLASSIFICATION_DEFS.get(`${kind}:${key}`);
}

const PRIORITY_MAP: Record<string, { label: string; color: ChipColor }> = {
  critical: { label: 'Critical', color: 'error'   },
  high:     { label: 'High',     color: 'warning' },
  normal:   { label: 'Normal',   color: 'muted'   },
  low:      { label: 'Low',      color: 'muted'   },
};

export function PriorityBadge({ priority }: { priority: string }) {
  const m = classificationDef('priority', priority)
    ?? PRIORITY_MAP[priority]
    ?? { label: (priority || '—'), color: 'muted' as ChipColor };
  return <Chip label={m.label} color={m.color} />;
}

const TYPE_LABEL: Record<string, string> = {
  bug: 'Bug',
  feature_request: 'Feature',
  question: 'Question',
  feedback: 'Feedback',
  incident: 'Incident',
  request: 'Request',
  complaint: 'Complaint',
  other: 'Other',
};

export function TypeBadge({ type }: { type: string }) {
  const def = classificationDef('type', type);
  return (
    <Chip
      label={def?.label ?? TYPE_LABEL[type] ?? (type || '—').replace(/_/g, ' ')}
      color={def?.color ?? 'muted'}
    />
  );
}
